import { css } from 'aphrodite';
import moment from 'moment';
import * as React from 'react';
import { useCallback } from 'react';
import { useLambda } from '../../models/hooks/useLambda';
import { EditItemPenIcon } from '../../web/components/svgs/icons/EditItemPenIcon';
import { Button } from '../../web/components-v2/Button';
import { LoadingSpinner } from '../../web/components/LoadingSpinner';
import { ITimezoneData, TimezonePicker } from '../../web/components/TimezonePicker';
import { baseStyleSheet } from '../../web/styles/styles';
import { getSelectedDateAsString } from '../UiUtils';
import { CheckmarkIcon } from '../icons/CheckmarkIcon';
import { styleSheet as pageStyles } from '../styles/styles';
import { IAvailabilitySlot } from '../types';
import { styleSheet } from './styles';

interface IProps {
	availabilities: IAvailabilitySlot[];
	breakpoint?: 'mobile' | 'desktop';
	goToStep: (step: number) => () => void;
	hide?: boolean;
	onAvailabilityChange: (availability: IAvailabilitySlot) => void;
	onTimezoneChange: (timezone: ITimezoneData) => void;
	processing?: boolean;
	selectedAvailability?: IAvailabilitySlot;
	selectedDate?: moment.Moment;
	selectedTimezone?: string;
	showPriority?: boolean;
}

export const ScheduleMeetingAvailabilityPicker: React.FC<IProps> = ({
	availabilities = [],
	breakpoint = 'mobile',
	goToStep,
	hide,
	onAvailabilityChange,
	onTimezoneChange,
	processing,
	selectedAvailability,
	selectedDate,
	selectedTimezone,
	showPriority,
}) => {
	const [changingTimezone, , setChangingTimezone] = useLambda(false);

	const onAvailabilityClicked = (availability: IAvailabilitySlot) => () => {
		onAvailabilityChange(availability);
	};

	const onTimeZoneChanged = (tz: ITimezoneData) => {
		setChangingTimezone(false)();
		onTimezoneChange(tz);
	};

	const renderAvailabilities = useCallback(() => {
		if (availabilities.length === 0) {
			return <div className={css(styleSheet.noAvailabilities)}>There are no available times for this date.</div>;
		}

		return availabilities.map((a, i) => {
			const availability = a.time.tz(selectedTimezone);
			let backgroundColor = null;
			if (showPriority) {
				// Check if it's an overbook (on the 15-minute mark)
				if (availability.minutes() === 15) {
					backgroundColor = styleSheet.colorPriority2; // Yellow
				} else if (a.priority === 0 || a.priority === 1) {
					// 0 priority is reserved when someone is only able to book for themselves
					// Make these appear as the "best" regardless of their assigned priority.
					backgroundColor = styleSheet.colorPriority0; // Dark green
				} else {
					// All other priorities (2+)
					backgroundColor = styleSheet.colorPriority1; // Light green
				}
			}
			const isSelected =
				availability.format('MMM DD, YYYY h:mm A') === selectedAvailability?.time?.format('MMM DD, YYYY h:mm A');
			return (
				<div
					className={css(styleSheet.availabilityContainer, isSelected && styleSheet.availabilityContainerSelected)}
					key={i}
				>
					<Button
						onClick={onAvailabilityClicked(a)}
						styles={[styleSheet.availability, backgroundColor]}
						type='button'
						variant='ghost'
					>
						{availability.format('h:mm A')}
					</Button>
					{isSelected && <CheckmarkIcon className={css(styleSheet.availabilityCheck)} />}
				</div>
			);
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [availabilities, selectedAvailability, selectedTimezone, onAvailabilityClicked]);

	if (processing) {
		return (
			<div className={css(styleSheet.availabilitiesContainer, hide && pageStyles.hide)}>
				{processing && <LoadingSpinner type='small' className={css(baseStyleSheet.absoluteCenter)} />}
			</div>
		);
	}

	return (
		<div className={css(styleSheet.availabilitiesContainer, hide && pageStyles.hide)}>
			{!selectedDate ? (
				<div className={css(styleSheet.noAvailabilities)}>Select a date to see availabilities.</div>
			) : (
				<div>
					<div className={css(styleSheet.availabilitiesHeader)}>
						<p
							className={css(pageStyles.label, styleSheet.selectedDateString)}
							onClick={breakpoint === 'mobile' ? goToStep(1) : null}
						>
							{getSelectedDateAsString(selectedDate?.tz(selectedTimezone), breakpoint)}
						</p>
						{changingTimezone ? (
							<div className={css(styleSheet.timezonePickerContainer)}>
								<TimezonePicker styles={[styleSheet.timezonePicker]} onTimezoneSelected={onTimeZoneChanged} />
								<button
									className={`change-timezone-button ${css(baseStyleSheet.link, styleSheet.link)}`}
									onClick={setChangingTimezone(false)}
								>
									Cancel
								</button>
							</div>
						) : (
							<div className={css(styleSheet.timezoneNoEdit)}>
								<p className={css(pageStyles.label, styleSheet.timezoneLabel)}>{`Time zone: ${selectedTimezone}`}</p>
								<Button
									aria-label='Change time zone'
									onClick={setChangingTimezone(true)}
									size='icon'
									styles={[styleSheet.changeTimezoneButton]}
									title='Change time zone'
									type='button'
									variant='ghost'
								>
									<EditItemPenIcon />
								</Button>
							</div>
						)}
					</div>
					<div className={css(styleSheet.availabilities)}>{renderAvailabilities()}</div>
				</div>
			)}
		</div>
	);
};
